/**
 * @file       secureHeader component
 * @version    2.0.0
 * @author     Mehran Majidi
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or
 *             altered in anyway without expressed written permission from
 *             MindShare HDV LLC.
 */
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from
    '@angular/core';
import { Chart } from 'chart.js';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { User } from '@app/model/user';
import { AuthService } from '@app/service/auth.service'
import { PermissionsService } from '@app/service/permission.service'
import { Inspection } from '@app/secure/interface/Inspection.interface';
import { InspectionService } from '@app/secure/service/inspection.service';
import { PreferenceService } from '@app/service/preference.service';
import { HomepageService } from '@app/service/homepage.service';
import { SettingService } from '@app/service/setting.service';
import { ShareModule } from '@app/secure/share/share.module';
import { PERMISSION_MODULE } from '@app/constants/Permission.constant';

@Component
({
    selector: 'secure-app-header',
    standalone: true,
    imports: [
        ShareModule
    ],
    templateUrl: './secureHeader.component.html',
    styleUrls: ['./secureHeader.component.scss']
})
class SecureHeaderComponent implements OnInit, OnDestroy
{
    public permissionModule = PERMISSION_MODULE;

    secure : boolean;
    super  : boolean;

    private headerSubs = new Subscription();

    // this should be an interface, for now an object
    activeInspection: Inspection;

    /**
     * @desc   - component constructor
     * @access - public
     * @param  -
     * @return -
     */
    //______________________ C O N S T R U C T O R _____________________________
    /**
     * @desc   - Component constructor
     * @access - public
     * @param  - router : Router for navigation
     *         - authService : service for authorization
     *         - permissionService : service for Cypher permissions
     *         - preferenceService : service for user preferences
     *         - inspectionService : service for VIN inspection
     *         - hpmepageService : service for navigation to cypher court
     * @return - void
     */
    constructor
    (
        private router: Router,
        private authService: AuthService,
        private permissionsService: PermissionsService,
        private preferenceService: PreferenceService,
        private inspectionService: InspectionService,
        private homepageService: HomepageService,
        private settingService: SettingService,
        private cdr: ChangeDetectorRef
    )
    {}


    /**
     * @desc   - angular initializer
     * @access - public
     * @param  -
     * @return -
     */
    ngOnInit()
    {
        let currentUser;
        let hasUser;

        currentUser = this.authService.userValue;
        this.secure = true;
        hasUser = !!currentUser;

        if (hasUser)
        {
            this.super = User.isSuper(currentUser.role);
        }
        else
        {
            this.super = false;
        }
/*
        if (currentUser.role == 1)
        {
            this.super = true;
        }
        else
        {
            this.super = false;
        }
  */
        this.headerSubs.add(
            this.inspectionService.inspection$
            .subscribe((inspection) =>
            {
                this.activeInspection = inspection;
            }));

        this.headerSubs.add(
            this.settingService.settings$.subscribe(() =>
            {
                this.cdr.detectChanges();
            }));

        this.headerSubs.add(
            this.permissionsService.permissions$.subscribe(() =>
            {
                this.cdr.detectChanges();
            }));

        this.permissionsService.loadPermissions();
        this.preferenceService.loadPreferences();
        this.settingService.getSettings();
    }

    /**
     * @desc   - release subscriptions
     * @access - public
     * @param  -
     * @return -
     */
    ngOnDestroy()
    {
        this.headerSubs.unsubscribe();
    }

    /**
     * @desc   - user has super user
     * @access - public
     * @param  - string moduleName: module name
     * @return - boolean superUser: true the user has a super user role
     */
    isSuper()
    {
        let user;
        let superUser;
        let hasUser;

        user = this.authService.userValue;
        hasUser = !!user;

        if (hasUser)
        {
            superUser = User.isSuper(user.role);
        }
        else
        {
            superUser = false;
        }

        return superUser
    }

    /**
     * @desc   - check if the user is a prosecutor
     * @access - public
     * @param  -
     * @return - boolean isProsecutor: true if the user is a prosecutor
     */
    isProsecutor()
    {
        return this.authService.isProsecutor();
    }


    /**
     * @desc   - determine if the user has permission on the module
     * @access - public
     * @param  - string moduleName: permission name (see PERMISSION_MODULE).
     * @return - boolean permitted: true if allowed for the permission.
     */
    hasPermission
    (
        moduleName
    )
    {
        let permitted: boolean;
        let isJuryModule: boolean;
        let isCaseQueueModule: boolean;
        let isProbationModule: boolean;
        let isCaseActionModule: boolean;
        let isCitationModule: boolean;
        let isDocketModule: boolean;
        let systemSettingEnabled: boolean;

        isJuryModule =
            moduleName ===
            PERMISSION_MODULE.JURY_SYSTEM;
        isCaseQueueModule =
            moduleName ===
            PERMISSION_MODULE.CASE_QUEUE;
        isProbationModule =
            moduleName ===
            PERMISSION_MODULE
                .PROBATION_SYSTEM;
        isCaseActionModule =
            moduleName ===
            PERMISSION_MODULE
                .CASE_ACTION;
        isCitationModule =
            moduleName ===
            PERMISSION_MODULE.CITATION;
        isDocketModule =
            moduleName ===
            PERMISSION_MODULE.DOCKET;

        if (isJuryModule)
        {
            systemSettingEnabled =
                this.settingService
                    .isJurySystemEnabled();

            if (!systemSettingEnabled)
            {
                permitted = false;
            }
            else
            {
                permitted =
                    this.permissionsService
                        .hasPermission(
                            moduleName
                        );
            }
        }
        else if (isCaseQueueModule)
        {
            systemSettingEnabled =
                this.settingService
                    .isCaseQueueEnabled();

            if (!systemSettingEnabled)
            {
                permitted = false;
            }
            else
            {
                permitted =
                    this.permissionsService
                        .hasPermission(
                            moduleName
                        );
            }
        }
        else if (isProbationModule)
        {
            systemSettingEnabled =
                this.settingService
                    .isProbationSystemEnabled();

            if (!systemSettingEnabled)
            {
                permitted = false;
            }
            else
            {
                permitted =
                    this.permissionsService
                        .hasPermission(
                            moduleName
                        );
            }
        }
        else if (isCaseActionModule)
        {
            systemSettingEnabled =
                this.settingService
                    .isAuditSystemEnabled();

            if (!systemSettingEnabled)
            {
                permitted = false;
            }
            else
            {
                permitted =
                    this.permissionsService
                        .hasPermission(
                            moduleName
                        );
            }
        }
        else if (isCitationModule)
        {
            systemSettingEnabled =
                this.settingService
                    .isCourtSystemEnabled();

            if (!systemSettingEnabled)
            {
                permitted = false;
            }
            else
            {
                permitted =
                    this.permissionsService
                        .hasPermission(
                            moduleName
                        );
            }
        }
        else if (isDocketModule)
        {
            systemSettingEnabled =
                this.settingService
                    .isDocketSystemEnabled();

            if (!systemSettingEnabled)
            {
                permitted = false;
            }
            else
            {
                permitted =
                    this.permissionsService
                        .hasPermission(
                            moduleName
                        );
            }
        }
        else
        {
            permitted =
                this.permissionsService
                    .hasPermission(moduleName);
        }

        return permitted;
    }

    /**
     * @desc   - naviagte to dashboard page
     * @access - public
     * @param  -
     * @return -
     */
    gotoDashboard()
    {
        this.router.navigate(['/dashboard']);
    }


    /**
     * @desc   - naviagte to setting page
     * @access - public
     * @param  -
     * @return -
     */
    gotoSetting()
    {
        this.router.navigate(['/settings']);
    }

    /**
     * @desc   - Whether editor may open user permission admin
     * @access - public
     * @param  -
     * @return - boolean has User module permission
     */
    hasUserAdminAccess()
    {
        let result;

        result = this.permissionsService.hasPermission(
            this.permissionModule.USER
        );

        return result;
    }

    /**
     * @desc   - Navigate to users list for permission admin
     * @access - public
     * @param  -
     * @return -
     */
    gotoUsers()
    {
        this.router.navigate(['/users']);
    }


    /**
     * @desc   - naviagte to inspection calendar page
     * @access - public
     * @param  -
     * @return -
     */
    gotoInspectionsCalendar()
    {
        this.router.navigate(['/inspectionsCalendar']);
    }

    /**
     * @desc   - naviagte to inspections page
     * @access - public
     * @param  -
     * @return -
     */
    gotoInspections()
    {
        this.router.navigate(['/inspections']);
    }

    /**
     * @desc   - naviagte to inspection page
     * @access - public
     * @param  -
     * @return -
     */
    gotoInspection()
    {
        let url;
        let inspection;

        // this is temporary
        inspection = {
            number: null,
            typeREF: null,
            statusREF: null,
            totalAmount: null,
            totalPaid: null,
            balance: null,
            customerUId: null,
            customerNumber: null,
            customerFirstName: null,
            customerLastName: null,
            officerUId: null,
            officerNumber: null,
            officerFirstName: null,
            officerLastName: null,
            startDate: null,
            startTime: null,
            endDate: null,
            endTime: null,
            duration: null
        }

        url = this.router.url;

        this.inspectionService.setInspection(inspection);

        // TODO for now, use reload until a better solution to force reload
        // from the route
        if (url == '/inspection')
        {
            location.reload();
        }
        else
        {
            this.router.navigate(['/inspection']);
        }
    }

    /**
     * @desc   - naviagte to citations page
     * @access - public
     * @param  -
     * @return -
     */
    gotoCitations()
    {
        this.router.navigate(['/citations']);
    }

    /**
     * @desc   - naviagte to messages page
     * @access - public
     * @param  -
     * @return -
     */
    gotoMessages()
    {
        this.router.navigate(['/messages']);
    }

    gotoTickets()
    {
        this.router.navigate(['/tickets']);
    }

    /**
     * @desc   - naviagte to compliance page
     * @access - public
     * @param  -
     * @return -
     */
    gotoCompliance()
    {
        this.router.navigate(['/compliance']);
    }

    /**
     * @desc   - naviagte to analytic page
     * @access - public
     * @param  -
     * @return -
     */
    gotoAnalytic()
    {
        this.router.navigate(['/analytic']);
    }

    /**
     * @desc   - naviagte to court queue page
     * @access - public
     * @param  -
     * @return -
     */
    gotoCaseQueue()
    {
        this.router.navigate(['/caseQueue']);
    }

    /**
     * @desc   - navigate to form templates list
     * @access - public
     * @param  -
     * @return -
     */
    gotoFormTemplates()
    {
        this.router.navigate(['/formTemplates']);
    }

    /**
     * @desc   - Forms menu (same gate as citations / court system)
     * @access - public
     * @param  -
     * @return - boolean
     */
    hasFormsMenuAccess()
    {
        let allowed;

        allowed =
            this.hasPermission(
                PERMISSION_MODULE.CITATION
            );

        return allowed;
    }

    gotoCaseProcessing()
    {
        this.homepageService
            .homepageProccessing(this.authService.userValue.uId);
    }

    /**
     * @desc   - naviagte to case motion page
     * @access - public
     * @param  -
     * @return -
     */
    gotoProbation()
    {
        this.router.navigate(['/probations']);
    }

    /**
     * @desc   - naviagte to case actions page
     * @access - public
     * @param  -
     * @return -
     */
    gotoCaseActions()
    {
        this.router.navigate(['/caseActions']);
    }

    /**
     * @desc   - naviagte to dockets page
     * @access - public
     * @param  -
     * @return -
     */
    gotoDockets()
    {
        this.router.navigate(['/dockets']);
    }

    /**
     * @desc   - naviagte to court calendar page
     * @access - public
     * @param  -
     * @return -
     */
    gotoCourtCalendar()
    {
        this.router.navigate(['/courtCalendar']);
    }

    /**
     * @desc   - checks if the court calendar app
     *           should be visible
     * @access - public
     * @param  -
     * @return - boolean
     */
    isCourtCalendarVisible()
    {
        let visible: boolean;

        visible =
            this.settingService
                .isCourtCalendarEnabled();

        return visible;
    }

    /**
     * @desc   - naviagte to prosecutor calendar page
     * @access - public
     * @param  -
     * @return -
     */
    gotoProsecutorCalendar()
    {
        this.router.navigate(['/prosecutorCalendar']);
    }

    /**
     * @desc   - naviagte to jury module page
     * @access - public
     * @param  -
     * @return -
     */
    gotoJuryModule()
    {
        this.router.navigate(['/juryModule']);
    }

    /**
     * @desc   - signout the user
     * @access - public
     * @param  -
     * @return -
     */
    signOut()
    {
        this.authService.logout();
    }
}
export { SecureHeaderComponent }